<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { computed, ref, unref, watch } from 'vue';
import { useTimeout } from '@vueuse/core';
import Chevron from '@/components/icons/Chevron.vue';
import BFactorPlanItem from '@/components/BFactorPlanItem.vue';

const props = defineProps<{
  data: any;
  expanded?: boolean;
}>();

const open = ref(props.expanded || false);

// Parse sambandsfaktorer from factors array - only show selected factors
const selectedFactors = computed(() => {
  if (!props.data?.factors || !Array.isArray(props.data.factors) || props.data.factors.length === 0) {
    return [];
  }
  
  console.log('🔍 Parsing sambandsfaktorer from factors array:', props.data.factors);
  
  // Show all factors that were selected by the user (have value and vision)
  // Remove the condition that filters out factors where value === vision
  const selected = props.data.factors.filter((factor: any) => 
    factor.label && 
    factor.value !== undefined && 
    factor.vision !== undefined
    // Removed: factor.value !== factor.vision - this was causing factors to be hidden
  );
  
  console.log('🎯 Selected factors (user choices):', selected);
  return selected;
});

const hasFactors = computed(() => selectedFactors.value.length > 0);

const displayDate = computed(() => {
  const d = props.data.date || props.data.target_date || props.data.goal_date || props.data.due_date || '';
  if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
  return d;
});

const classes = computed(() => {
  return [
    'flex',
    'items-center',
    'w-full',
    open.value ? 'rounded-t-md' : 'rounded-md',
    'text-base',
    'bg-yellow-light',
    'text-yellow-darker',
    'px-4',
    'py-3',
    'font-medium',
  ];
});
</script>

<template>
  <li class="block shadow-md rounded-md">
    <button :class="classes" @click="open = !open">
      <div class="flex flex-col items-start text-left">
        <slot></slot>
        <span class="text-sm text-gray-600">
          {{ data.value }} → {{ data.vision }}
        </span>
      </div>

      <Chevron class="ml-auto" :direction="open ? 'up' : 'down'" />
    </button>

    <div v-show="open" class="p-6 rounded-b-md bg-white">
      <h2 class="mt-0 font-light text-xl">
        Jag ska förbättra
        <span class="text-yellow-darker font-medium">{{ data.label }}</span>
      </h2>

      <p v-if="displayDate">Målet ska uppnås {{ displayDate }}.</p>

      <!-- Show action plan if exists -->
      <div v-if="data.action_plan" class="action-plan mt-4">
        <h3 class="text-lg font-medium mb-2">Min plan:</h3>
        <p class="text-gray-700">{{ data.action_plan }}</p>
      </div>

      <!-- Show selected factors -->
      <template v-if="hasFactors">
        <h3 class="text-lg font-medium mt-4 mb-2">Valda förbättringsområden:</h3>
        <ul class="flex flex-col m-0 p-0 gap-y-2">
          <li class="list-none" v-for="factor in selectedFactors" :key="factor.category_id">
            <div class="flex flex-col">
              <div class="text-yellow-darker">
                {{ factor.label }}
                <span class="text-sm text-gray-600">
                  {{ factor.value }} → {{ factor.vision }}
                </span>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </div>
  </li>
</template>

<style scoped>
.text-yellow-darker {
  color: #6B3100;
}

.text-gray-600 {
  color: #666;
}

.text-gray-700 {
  color: #4a5568;
}

.action-plan {
  background-color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}
</style>